export type TenantEnvironment = 'test' | 'prod'

/**
 * Resolves the tenant environment from an API hostname, mirroring the
 * `*.test.skl.io` / localhost convention used across Skedulo's test
 * environments - anything else is treated as production.
 */
export const resolveTenantEnvironment = (
  hostname: string,
): TenantEnvironment =>
  hostname === 'localhost' || hostname.endsWith('.test.skl.io')
    ? 'test'
    : 'prod'

/** Stands in for the tenant when no readable session is stored. */
export const UNKNOWN_TENANT_ID = 'unknown-tenant'

const ORGANISATION_CLAIM = 'https://api.skedulo.com/organization_id'

interface StoredSession {
  profile?: Record<string, string | undefined>
}

/**
 * Reads the tenant the stored session belongs to. A host that serves several
 * tenants from one origin - `localhost` and the branch previews, which take the
 * tenant from the URL rather than the hostname - replaces this session as the
 * tenant changes, so anything keyed by it stays separate per tenant.
 */
export const readCurrentTenantId = (): string => {
  try {
    const session: StoredSession | null = JSON.parse(
      localStorage.getItem('auth') || 'null',
    )
    return session?.profile?.[ORGANISATION_CLAIM] || UNKNOWN_TENANT_ID
  } catch {
    return UNKNOWN_TENANT_ID
  }
}

// TODO: Determine relevance of these utils after protobuf rework using ts-proto

// Copied from some server side code - sorts the most relevant items first
export const compareByRelevance = <
  Item extends { userId?: string; domainId?: string; tenantId?: string },
>(
  a: Item,
  b: Item,
): number => {
  if (a.userId && !b.userId) {
    return -1
  }
  if (!a.userId && b.userId) {
    return 1
  }
  if (a.domainId && !b.domainId) {
    return -1
  }
  if (!a.domainId && b.domainId) {
    return 1
  }
  if (a.tenantId && !b.tenantId) {
    return -1
  }
  if (!a.tenantId && b.tenantId) {
    return 1
  }
  return 0
}
